import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button, Card, Input } from '@/components/ui';

/**
 * The trial clock, and the one question a new account has to answer.
 *
 * Shown on the client dashboard until the capacity step is done. It keeps
 * asking rather than offering once and forgetting: an account that never said
 * what it needs is one nobody can quote, and the answer is the whole basis of
 * the first invoice.
 */

interface Onboarding {
  on_trial: boolean;
  needs_capacity: boolean;
  trial_expired: boolean;
  trial_days_left: number | null;
  trial_ends_at: string | null;
  max_bots: number | null;
  max_concurrent_calls: number | null;
}

export function useOnboarding() {
  return useQuery({
    queryKey: ['onboarding'],
    queryFn: () => api.get<Onboarding>('/onboarding'),
    // It changes when an operator approves something or a trial rolls over,
    // neither of which happens while somebody is looking at the page.
    staleTime: 60_000,
  });
}

export default function OnboardingBanner() {
  const queryClient = useQueryClient();
  const { data } = useOnboarding();
  const [open, setOpen] = useState(false);
  const [bots, setBots] = useState(2);
  const [agents, setAgents] = useState(1);
  const [concurrency, setConcurrency] = useState(5);
  const [notes, setNotes] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api.post('/onboarding/capacity', {
        requested_bots: bots,
        requested_agents: agents,
        requested_concurrency: concurrency,
        requested_notes: notes || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      setOpen(false);
    },
  });

  if (!data) return null;
  if (!data.needs_capacity && !data.on_trial) return null;

  const days = data.trial_days_left ?? 0;

  return (
    <Card
      className="mb-4 border-brand-300 dark:border-brand-800/60"
      title={
        data.needs_capacity
          ? 'Tell us how much capacity you need'
          : data.trial_expired
            ? 'Your trial has ended'
            : `Your trial: ${days} day${days === 1 ? '' : 's'} left`
      }
    >
      {data.needs_capacity ? (
        <>
          <p className="text-sm text-ink-600 dark:text-ink-300">
            How many bots do you want running, and how many calls at once? We
            size your account and quote from this, so it is worth a moment's
            thought rather than a guess.
            {data.on_trial && !data.trial_expired ? (
              <>
                {' '}
                Your trial has {days} day{days === 1 ? '' : 's'} left.
              </>
            ) : null}
          </p>

          {!open ? (
            <Button className="mt-3" onClick={() => setOpen(true)}>
              Choose your capacity
            </Button>
          ) : (
            <div className="mt-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-xs text-ink-600 dark:text-ink-300">
                  Bots
                  <Input
                    type="number"
                    min={0}
                    value={bots}
                    onChange={(e) => setBots(Number(e.target.value))}
                    className="mt-1"
                  />
                </label>
                <label className="text-xs text-ink-600 dark:text-ink-300">
                  Voice agents
                  <Input
                    type="number"
                    min={0}
                    value={agents}
                    onChange={(e) => setAgents(Number(e.target.value))}
                    className="mt-1"
                  />
                </label>
                <label className="text-xs text-ink-600 dark:text-ink-300">
                  Calls at once
                  <Input
                    type="number"
                    min={0}
                    value={concurrency}
                    onChange={(e) => setConcurrency(Number(e.target.value))}
                    className="mt-1"
                  />
                </label>
              </div>
              <label className="mt-3 block text-xs text-ink-600 dark:text-ink-300">
                Anything else we should know
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Which verticals, how many agents, when you want to go live…"
                  className="mt-1"
                />
              </label>

              {/* Said before they submit, not after. A client who asks for ten
                  bots and sees nothing change would reasonably assume it
                  worked. */}
              <p className="mt-2 text-xs text-ink-500">
                Your account currently allows {data.max_bots ?? 0} bot
                {data.max_bots === 1 ? '' : 's'} and{' '}
                {data.max_concurrent_calls ?? 0} simultaneous call
                {data.max_concurrent_calls === 1 ? '' : 's'}. Those limits stay
                as they are until our team confirms the change with you.
              </p>

              {save.error ? (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  {(save.error as Error).message}
                </p>
              ) : null}

              <div className="mt-3 flex gap-2">
                <Button onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? 'Sending…' : 'Send to our team'}
                </Button>
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  Not now
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-ink-600 dark:text-ink-300">
          {data.trial_expired
            ? 'Nothing has been switched off — your bots keep running. Your first invoice is on its way and is visible under Billing.'
            : `Everything is running normally. When the trial ends on ${
                data.trial_ends_at
                  ? new Date(data.trial_ends_at).toLocaleDateString()
                  : 'its end date'
              } we raise your first invoice; nothing is switched off.`}
        </p>
      )}
    </Card>
  );
}
